import classNames from 'classnames';
import * as React from 'react';
import tabsStyles from '../BookmarkTabs/BookmarkTabs.module.css';
import { TabPanel } from '../Tabs/TabPanel';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { BookmarkTabProps, BookmarkTabsProps } from '~/Partials/BookmarkTabs/BookmarkTabs';
import { BookmarkTabs } from '~/Partials/BookmarkTabs/BookmarkTabs';
import type { TabKey } from '~/Partials/TabList/Tab';
import type { Theme } from '~/Utils/Theme';
import styles from './BookmarkContainer.module.css';
import type { BookmarkContainerVariant } from './BookmarkContainerVariant';

export interface BookmarkContainerProps extends Pick<BookmarkTabsProps, 'tabs' | 'currentlySelectedTab'> {
    tabsLabel: string;
    setSelectedTab: (key: TabKey) => void;
    upperDescription?: string;
    HiddenTabContent?: React.FC<BookmarkTabProps>; // Useful for the SEO purposes (mainly crawling)
    variant?: BookmarkContainerVariant;
    className?: string;
    contentClassName?: string;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const BookmarkContainer: React.FC<React.PropsWithChildren<BookmarkContainerProps>> = props => {
    const contextualTheme = useTheme();
    const {
        tabsLabel,
        tabs,
        currentlySelectedTab,
        setSelectedTab,
        upperDescription,
        HiddenTabContent,
        children,
        className,
        contentClassName,
        variant = 'dark',
        theme = contextualTheme,
    } = props;

    const { withRoundedCorners } = theme;

    const hasMoreThanOneTab = tabs.length > 1;

    return (
        <div
            className={classNames(
                styles.BookmarkContainer,
                styles[`Variant--${variant}`],
                withRoundedCorners && styles['Variant--rounded'],
                withRoundedCorners && tabsStyles.ContainerWithTabBottomHack,
                className
            )}
        >
            <div className={styles.Wrapper}>
                <div className={styles.UpperSection}>
                    {hasMoreThanOneTab && (
                        <BookmarkTabs
                            label={tabsLabel}
                            tabs={tabs}
                            onSelect={setSelectedTab}
                            currentlySelectedTab={currentlySelectedTab}
                            variant={variant}
                        />
                    )}
                    {upperDescription && <span className={styles.UpperDescription}>{upperDescription}</span>}
                </div>
                {hasMoreThanOneTab ? (
                    <TabPanel tabKey={currentlySelectedTab} className={classNames(styles.Content, contentClassName)}>
                        {children}
                    </TabPanel>
                ) : (
                    <div className={classNames(styles.Content, contentClassName)}>{children}</div>
                )}
                {HiddenTabContent && (
                    <div className={styles.HiddenContent}>
                        {tabs
                            .filter(tab => tab.uniqueId !== currentlySelectedTab)
                            .map(tab => (
                                <TabPanel key={tab.uniqueId} tabKey={tab.uniqueId}>
                                    <HiddenTabContent {...tab} />
                                </TabPanel>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
};
